import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';

import ApprovalStepEditor from './ApprovalStepEditor';

const DOC_TYPE_OPTIONS = [
  { value: '', label: '전체(지정 안 함)' },
  { value: 'ORDER_BOOK', label: '수주품의서' },
  { value: 'HR_LEAVE', label: '휴가' },
];

// 공개 범위(LINE_SCOPE). 이슈 #7(2026-10-21) - 기존엔 이 값이 DB에 저장만 되고 조회에서
// 전혀 쓰이지 않아(항상 CRT_USR 본인만) 템플릿 공유가 안 됐다. 이제 선택한 범위대로 다른
// 사용자에게도 보인다(백엔드 selectMyApprovalLines/submitDocument 가시성 판정).
const SCOPE_OPTIONS = [
  { value: 'PRIVATE', label: '개인(본인만)' },
  { value: 'DEPARTMENT', label: '부서(같은 부서 전체)' },
  { value: 'COMPANY', label: '전체(회사 전체)' },
];

const emptyForm = { lineNm: '', lineScope: 'PRIVATE', docType: '', steps: [] };

// 결재선 템플릿 생성/수정 다이얼로그. ksm_frontend ApprovalLineTemplateModal.js 포팅
// (2026-10-20 결재 V2 이관) - bito는 조직 역할/직위 기반 지정은 쓰지 않고 사용자 지정만 지원한다.
const ApprovalLineTemplateModal = ({ open, line, currentUserUuid, onClose, onSave }) => {
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    if (line) {
      setForm({
        lineNm: line.lineNm || '',
        lineScope: line.lineScope || 'PRIVATE',
        docType: line.docType || '',
        steps: (line.steps || []).map((s) => ({
          assigneeUsrUuid: s.assigneeUsrUuid,
          assigneeUsrNm: s.assigneeUsrNm,
        })),
      });
    } else {
      setForm(emptyForm);
    }
  }, [line, open]);

  const handleSave = () => {
    onSave({
      lineNm: form.lineNm,
      lineScope: form.lineScope,
      docType: form.docType || null,
      steps: form.steps,
    });
  };

  const validSteps = form.steps.filter((s) => s.assigneeUsrUuid);
  const canSave = form.lineNm.trim() && validSteps.length > 0 && validSteps.length === form.steps.length;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{line ? '결재선 템플릿 수정' : '결재선 템플릿 추가'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="템플릿 이름"
            value={form.lineNm}
            onChange={(e) => setForm({ ...form, lineNm: e.target.value })}
            required
            fullWidth
          />
          <TextField
            select
            label="대상 문서"
            value={form.docType}
            onChange={(e) => setForm({ ...form, docType: e.target.value })}
            fullWidth
          >
            {DOC_TYPE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="공개 범위"
            value={form.lineScope}
            onChange={(e) => setForm({ ...form, lineScope: e.target.value })}
            helperText="부서/전체로 설정하면 다른 사용자도 이 템플릿을 결재선으로 선택할 수 있습니다. 수정·삭제는 작성자만 가능합니다."
            fullWidth
          >
            {SCOPE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
          <ApprovalStepEditor
            steps={form.steps}
            onChange={(steps) => setForm({ ...form, steps })}
            excludeUsrUuid={currentUserUuid}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          취소
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={!canSave}>
          저장
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ApprovalLineTemplateModal;
